import {
  amnVendor,
  languageLineVendor,
  useStartupConfigurationStore,
} from "../../pages/stores/startupConfiguration"
import type { TgaParams, UrlParams, VendorParams } from "./UrlParams"
import { parseAmnParams } from "./amn"
import { parseLanguageLineParams } from "./languageLine"
import { useMemo } from "react"
import type { useAppContext } from "../../AppContext"
import { logger } from "../../logger"

/**
 * Used to load url params into context by the parent App on application
 * startup. To read these params in other components, use {@link useAppContext}.
 */
export const useUrlParams = (): UrlParams => {
  const vendor = useStartupConfigurationStore()?.ui_profile.vendor

  const urlParams = useMemo(() => {
    const params = parseUrlParams({ url: window.location.href, vendor })
    if (vendor)
      logger.info("Parsed vendor url params", {
        vendor,
        region: params.region,
        hasGuestToken: Boolean(params.guestToken),
        hasParticipantName: Boolean(params.participantName),
        startCallOnMute: params.startCallOnMute,
        startWithVideo: params.startWithVideo,
        initialVolume: params.initialVolume,
      })
    return params
  }, [vendor])

  return urlParams
}

export const parseUrlParams = ({
  url,
  vendor,
}: {
  url: string
  vendor: string | undefined
}): UrlParams => {
  const tgaParams = parseTgaParams(url)
  const vendorParams = parseVendorParams(url, vendor)
  return { ...tgaParams, ...vendorParams }
}

const parseTgaParams = (url: string): TgaParams => {
  const { searchParams } = new URL(url)
  return {
    guestToken: searchParams.get("g") ?? undefined,
    region: searchParams.get("r")?.toLowerCase() ?? undefined,
  }
}

const parseVendorParams = (
  url: string,
  vendor: string | undefined
): VendorParams => {
  // For at least one release we should fall back to LanguageLine parsing
  // because in prior releases we always parsed LanguageLine props as the
  // "vendor" property did not yet exist. We can remove this fallback once
  // the server is surely passing the new vendor parameter.
  const resolvedVendor = vendor ?? languageLineVendor

  switch (resolvedVendor) {
    case amnVendor:
      return parseAmnParams(url)
    case languageLineVendor:
      return parseLanguageLineParams(url)
    default:
      return { urlWithoutVendorParams: url }
  }
}
